/**
 * Reliability Presentation
 *
 * Pure functions that turn reliability domain models into wording, grouping and
 * proportions for the UI. No React or I/O here.
 */
import type {
  BayActivity,
  BayConcern,
  BayReliability,
  DelayEvidence,
  ReliabilityLevel,
  ReliabilityReason,
  SiteReliabilityHint,
} from '../models/reliabilityModels';
import type { SuperBay } from '../models/domainModels';

export function levelLabel(level: ReliabilityLevel): string {
  switch (level) {
    case 'Ok':
      return 'OK';
    case 'Suspect':
      return 'Suspect';
    case 'Unknown':
      return 'Unknown';
    default:
      return assertNever(level);
  }
}

export function reasonLabel(reason: ReliabilityReason): string {
  switch (reason) {
    case 'SiteOffline':
      return 'Site offline';
    case 'AllBaysTrackingOffline':
      return 'Tracking offline on all bays';
    case 'BayTrackingOffline':
      return 'Bay tracking offline';
    case 'ShotsDelayed':
      return 'Shots delayed';
    default:
      return assertNever(reason);
  }
}

function countSuspectBays(bays: readonly BayReliability[]): number {
  return bays.filter((bay) => bay.level === 'Suspect').length;
}

/**
 * One-line verdict for the site. Site-level Ok means "no site-wide issue found", not
 * "every bay is fine", so an Ok site with Suspect bays says so.
 */
export function siteHeadline(
  site: SiteReliabilityHint,
  bays: readonly BayReliability[]
): string {
  switch (site.level) {
    case 'Ok': {
      const suspect = countSuspectBays(bays);
      return suspect === 0
        ? 'Operating normally'
        : `No site-wide issues · ${suspect} bay${suspect === 1 ? '' : 's'} with issues`;
    }
    case 'Unknown':
      return 'Status not confirmed';
    case 'Suspect':
      return site.reasons.length > 0
        ? site.reasons.map(reasonLabel).join(' · ')
        : 'Problem suspected';
    default:
      return assertNever(site.level);
  }
}

/** What the site-level hint means for the site's bays. */
export function siteExplanation(site: SiteReliabilityHint): string {
  switch (site.level) {
    case 'Ok':
      return 'No site-level problems detected.';
    case 'Unknown':
      return 'Nothing points to a problem, but there is no recent upstream status to confirm it.';
    case 'Suspect':
      return 'Site-level problems affect every bay at this site.';
    default:
      return assertNever(site.level);
  }
}

/**
 * Bay reasons explained by the site-level hint. Site reasons repeat on every bay, and
 * AllBaysTrackingOffline means each bay's BayTrackingOffline is part of the site issue.
 */
function siteCoveredReasons(site: SiteReliabilityHint): readonly ReliabilityReason[] {
  return site.reasons.includes('AllBaysTrackingOffline')
    ? [...site.reasons, 'BayTrackingOffline']
    : site.reasons;
}

/** Reasons a bay has beyond those explained by the site. */
export function bayOwnReasons(
  bay: BayReliability,
  site: SiteReliabilityHint
): readonly ReliabilityReason[] {
  const covered = siteCoveredReasons(site);
  return bay.reasons.filter((reason) => !covered.includes(reason));
}

export function classifyBayConcern(bay: BayReliability, site: SiteReliabilityHint): BayConcern {
  switch (bay.level) {
    case 'Ok':
      return 'ok';
    case 'Unknown':
      return 'unknown';
    case 'Suspect':
      return site.level === 'Suspect' && bayOwnReasons(bay, site).length === 0
        ? 'site-wide'
        : 'bay-specific';
    default:
      return assertNever(bay.level);
  }
}

export interface BaySummary {
  readonly total: number;
  readonly ok: number;
  readonly siteWide: number;
  readonly baySpecific: number;
  readonly unknown: number;
}

export function summarizeBays(
  bays: readonly BayReliability[],
  site: SiteReliabilityHint
): BaySummary {
  const concerns = bays.map((bay) => classifyBayConcern(bay, site));
  const count = (concern: BayConcern): number => concerns.filter((c) => c === concern).length;
  return {
    total: bays.length,
    ok: count('ok'),
    siteWide: count('site-wide'),
    baySpecific: count('bay-specific'),
    unknown: count('unknown'),
  };
}

export function formatSeconds(ms: number): string {
  return `${(ms / 1000).toFixed(1)} s`;
}

export interface DelayAssessment {
  /** Enough samples for the backend to act on the median. */
  readonly isConclusive: boolean;
  readonly isOverThreshold: boolean;
  /** Bar fill (0–1) for the median. */
  readonly fillRatio: number;
  /** Marker position (0–1) for the threshold. */
  readonly thresholdRatio: number;
  readonly windowMinutes: number;
}

/** Headroom above the threshold so a median just under or over it is visually distinct. */
const SCALE_HEADROOM = 1.5;

export function assessDelay(delay: DelayEvidence): DelayAssessment {
  const median = delay.medianMs;
  const scaleMax = Math.max(delay.thresholdMs * SCALE_HEADROOM, median ?? 0);
  return {
    isConclusive: median !== undefined && delay.sampleCount >= delay.minSamples,
    isOverThreshold: median !== undefined && median >= delay.thresholdMs,
    fillRatio: median !== undefined && scaleMax > 0 ? median / scaleMax : 0,
    thresholdRatio: scaleMax > 0 ? delay.thresholdMs / scaleMax : 0,
    windowMinutes: Math.round(delay.windowS / 60),
  };
}

/** Display label for a bay id: the super bay name if it is one, otherwise "Bay {id}". */
export function bayLabel(bayId: string, superBays: readonly SuperBay[]): string {
  return superBays.find((superBay) => superBay.id === bayId)?.name ?? `Bay ${bayId}`;
}

/** Compact label for a grid tile: the super bay name if it is one, otherwise the bay id. */
export function bayShortLabel(bayId: string, superBays: readonly SuperBay[]): string {
  return superBays.find((superBay) => superBay.id === bayId)?.name ?? bayId;
}

/** Visual tone shared by pills, dots and tiles. */
export type StatusTone = 'ok' | 'warning' | 'problem' | 'neutral';

export function levelTone(level: ReliabilityLevel): StatusTone {
  switch (level) {
    case 'Ok':
      return 'ok';
    case 'Suspect':
      return 'warning';
    case 'Unknown':
      return 'neutral';
    default:
      return assertNever(level);
  }
}

/** Tone for the whole site, taking Suspect bays on an Ok site into account. */
export function overallTone(
  site: SiteReliabilityHint,
  bays: readonly BayReliability[]
): StatusTone {
  return site.level === 'Ok' && countSuspectBays(bays) > 0 ? 'warning' : levelTone(site.level);
}

export function concernTone(concern: BayConcern): StatusTone {
  switch (concern) {
    case 'ok':
      return 'ok';
    case 'site-wide':
      return 'warning';
    case 'bay-specific':
      return 'problem';
    case 'unknown':
      return 'neutral';
    default:
      return assertNever(concern);
  }
}

/** Short sentence summarising the bay grid, e.g. "1 with its own issue · 46 site-wide issue only". */
export function describeBaySummary(summary: BaySummary): string {
  if (summary.total === 0) return 'No bays configured';
  if (summary.ok === summary.total) {
    return summary.total === 1 ? 'The bay is OK' : `All ${summary.total} bays OK`;
  }
  if (summary.siteWide === summary.total) {
    return summary.total === 1
      ? 'The bay is affected only by the site-wide issue'
      : `All ${summary.total} bays affected only by the site-wide issue`;
  }
  const parts: readonly (readonly [number, string])[] = [
    [summary.baySpecific, `${summary.baySpecific} with ${summary.baySpecific === 1 ? 'its' : 'their'} own issue`],
    [summary.siteWide, `${summary.siteWide} site-wide issue only`],
    [summary.unknown, `${summary.unknown} unknown`],
    [summary.ok, `${summary.ok} OK`],
  ];
  return parts
    .filter(([count]) => count > 0)
    .map(([, text]) => text)
    .join(' · ');
}

const HOUR_MS = 60 * 60 * 1000;
const DAY_MS = 24 * HOUR_MS;

export type LastShotStatus =
  | { readonly isStale: false }
  | { readonly isStale: true; readonly text: string };

function plural(count: number, unit: string): string {
  return `${count} ${unit}${count === 1 ? '' : 's'}`;
}

function formatAgeInDays(ageMs: number): string {
  const days = Math.floor(ageMs / DAY_MS);
  if (days < 14) return plural(days, 'day');
  if (days < 60) return plural(Math.floor(days / 7), 'week');
  return plural(Math.floor(days / 30), 'month');
}

/**
 * Whether the site's last shot is old enough to point out next to the status.
 * The backend never flags a quiet site (closed and seasonal sites look the same),
 * so this is information, not a verdict.
 */
export function describeLastShot(lastShotAt: string | undefined, now: Date): LastShotStatus {
  if (lastShotAt === undefined) return { isStale: true, text: 'No shots delivered yet' };
  const time = new Date(lastShotAt).getTime();
  if (Number.isNaN(time)) return { isStale: false };
  const ageMs = now.getTime() - time;
  return ageMs > DAY_MS
    ? { isStale: true, text: `Last shot ${formatAgeInDays(ageMs)} ago` }
    : { isStale: false };
}

/** A bay counts as active when it tracked a shot within this window. */
export const ACTIVE_WINDOW_MS = 3 * 60 * 1000;

/**
 * Current activity at a bay. `reference` should be the assessment's evaluatedAt, so the
 * comparison uses server time on both sides and is unaffected by client clock skew.
 */
export function assessBayActivity(bay: BayReliability, reference: Date): BayActivity {
  if (bay.lastActivityAt === undefined) return 'idle';
  const time = new Date(bay.lastActivityAt).getTime();
  if (Number.isNaN(time) || reference.getTime() - time > ACTIVE_WINDOW_MS) return 'idle';
  return (bay.activeSessions ?? 0) > 0 ? 'go-in-use' : 'active';
}

/**
 * Upstream says the bay's tracking is offline, but it is tracking shots right now.
 * A quiet offline bay is probably really down, so it gets no callout.
 */
export function describeTrackingMismatch(
  bay: BayReliability,
  activity: BayActivity
): string | null {
  return bay.reasons.includes('BayTrackingOffline') && activity !== 'idle'
    ? 'Shots are still arriving, so the upstream tracking offline status may be stale or wrong.'
    : null;
}

export interface ActivitySummary {
  readonly active: number;
  readonly goInUse: number;
  readonly mismatched: number;
}

export function summarizeActivity(
  bays: readonly BayReliability[],
  reference: Date
): ActivitySummary {
  const assessed = bays.map((bay) => ({ bay, activity: assessBayActivity(bay, reference) }));
  return {
    active: assessed.filter(({ activity }) => activity !== 'idle').length,
    goInUse: assessed.filter(({ activity }) => activity === 'go-in-use').length,
    mismatched: assessed.filter(({ bay, activity }) => describeTrackingMismatch(bay, activity) !== null)
      .length,
  };
}

/** e.g. "3 bays tracking shots now · 1 with Go in use"; null when no bay is active. */
export function describeActivitySummary(summary: ActivitySummary): string | null {
  if (summary.active === 0) return null;
  return [
    `${plural(summary.active, 'bay')} tracking shots now`,
    summary.goInUse > 0 ? `${summary.goInUse} with Go in use` : null,
    summary.mismatched > 0 ? `${summary.mismatched} reported offline but tracking shots` : null,
  ]
    .filter((part): part is string => part !== null)
    .join(' · ');
}

const MINUTE_MS = 60 * 1000;

export interface LastShotTracked {
  readonly text: string;
  /** Absolute time, for a hover title. */
  readonly title?: string;
}

/** Relative time of the bay's last tracked shot, e.g. "12 min ago". */
export function formatLastShotTracked(
  lastActivityAt: string | undefined,
  now: Date
): LastShotTracked {
  if (lastActivityAt === undefined) return { text: 'No shots in the last 7 days' };
  const date = new Date(lastActivityAt);
  if (Number.isNaN(date.getTime())) return { text: lastActivityAt };

  const ageMs = now.getTime() - date.getTime();
  const text =
    ageMs < MINUTE_MS
      ? 'Just now'
      : ageMs < HOUR_MS
        ? `${Math.floor(ageMs / MINUTE_MS)} min ago`
        : ageMs < DAY_MS
          ? `${Math.floor(ageMs / HOUR_MS)} h ago`
          : `${plural(Math.floor(ageMs / DAY_MS), 'day')} ago`;
  return { text, title: date.toLocaleString() };
}

function sessionsText(activeSessions: number | undefined): string | null {
  if (activeSessions === undefined) return null;
  return `${activeSessions} active session${activeSessions === 1 ? '' : 's'}`;
}

function activityText(activity: BayActivity): string | null {
  switch (activity) {
    case 'go-in-use':
      return 'Shots tracked in the last 3 minutes, Go in use.';
    case 'active':
      return 'Shots tracked in the last 3 minutes.';
    case 'idle':
      return null;
    default:
      return assertNever(activity);
  }
}

const RECENCY_HALF_AT_MS = HOUR_MS;
const RECENCY_FLOOR_AT_MS = 7 * DAY_MS;
const RECENCY_FLOOR = 0.01;

/** Position of `ageMs` between `fromMs` and `toMs` on a log scale, 0–1. */
function logPosition(ageMs: number, fromMs: number, toMs: number): number {
  return Math.log(ageMs / fromMs) / Math.log(toMs / fromMs);
}

/**
 * How recently the bay tracked a shot, as a 0–1 bar length on a two-segment log scale:
 * 1 within the 3-minute activity window, 0.5 at 1 hour, 0.01 at 7 days (the retention
 * limit, also the floor while a shot is known), and 0 with no shot in the last 7 days.
 * Log scaling gives the last hour most of the bar, where minutes matter.
 */
export function activityRecency(lastActivityAt: string | undefined, reference: Date): number {
  if (lastActivityAt === undefined) return 0;
  const time = new Date(lastActivityAt).getTime();
  if (Number.isNaN(time)) return 0;

  const ageMs = reference.getTime() - time;
  if (ageMs <= ACTIVE_WINDOW_MS) return 1;
  if (ageMs <= RECENCY_HALF_AT_MS) {
    return 1 - 0.5 * logPosition(ageMs, ACTIVE_WINDOW_MS, RECENCY_HALF_AT_MS);
  }
  if (ageMs <= RECENCY_FLOOR_AT_MS) {
    return 0.5 - (0.5 - RECENCY_FLOOR) * logPosition(ageMs, RECENCY_HALF_AT_MS, RECENCY_FLOOR_AT_MS);
  }
  return RECENCY_FLOOR;
}

/** Sentence for the last tracked shot, e.g. "Last shot tracked 12 min ago". */
export function lastShotTrackedSentence(lastActivityAt: string | undefined, now: Date): string {
  const { text } = formatLastShotTracked(lastActivityAt, now);
  return lastActivityAt === undefined
    ? text
    : `Last shot tracked ${text.charAt(0).toLowerCase()}${text.slice(1)}`;
}

function withReasons(prefix: string, reasons: readonly ReliabilityReason[]): string {
  return reasons.length > 0 ? `${prefix}: ${reasons.map(reasonLabel).join(', ')}` : prefix;
}

/** Hover text for a bay's status dot: its concern and the reasons behind it. */
export function concernTooltip(bay: BayReliability, site: SiteReliabilityHint): string {
  const concern = classifyBayConcern(bay, site);
  switch (concern) {
    case 'ok':
      return 'OK: no problems detected';
    case 'site-wide':
      return withReasons('Site-wide issue only', bay.reasons);
    case 'bay-specific':
      return withReasons('Bay-specific issue', bayOwnReasons(bay, site));
    case 'unknown':
      return 'Unknown: no recent upstream status';
    default:
      return assertNever(concern);
  }
}

/** Hover text for a bay's session badge. */
export function sessionsTooltip(activeSessions: number, activity: BayActivity): string {
  const sessions = `${activeSessions} active Go session${activeSessions === 1 ? '' : 's'}`;
  return activity === 'go-in-use' ? `${sessions} · Go in use now` : sessions;
}

/** Hover text for a bay's time-since-last-shot bar: relative and absolute time. */
export function recencyTooltip(lastActivityAt: string, reference: Date): string {
  const { title } = formatLastShotTracked(lastActivityAt, reference);
  const sentence = lastShotTrackedSentence(lastActivityAt, reference);
  return title ? `${sentence} · ${title}` : sentence;
}

/**
 * Full description of a bay tile for assistive technology. `lastShot` describes the
 * last tracked shot and is only used for idle bays; active ones are described by activity.
 */
export function bayAccessibleLabel(
  label: string,
  bay: BayReliability,
  activity: BayActivity,
  lastShot?: string
): string {
  const reasons = bay.reasons.length > 0 ? `${bay.reasons.map(reasonLabel).join(', ')}.` : null;
  const sessions = sessionsText(bay.activeSessions);
  return [
    `${label}: ${levelLabel(bay.level)}.`,
    reasons,
    sessions && `${sessions}.`,
    activityText(activity),
    activity === 'idle' && lastShot ? `${lastShot}.` : null,
  ]
    .filter((part): part is string => part !== null)
    .join(' ');
}

function assertNever(value: never): never {
  throw new Error(`Unhandled value: ${String(value)}`);
}
